/**
 * Single source of truth for the invoice page geometry and body type.
 *
 * The on-screen editor and the exported PDF have to lay out identically, so
 * both read these values rather than keeping their own copies:
 *
 *   - the editor applies `INVOICE_PAGE_VARS` to its root, and
 *     invoice-editor.scss consumes the resulting CSS custom properties;
 *   - InvoicePDF converts them to points with the helpers below.
 *
 * Change a value here and both surfaces move together.
 */

// A4
export const PAGE_WIDTH_MM = 210;
export const PAGE_HEIGHT_MM = 297;

// Page margins. The bottom is deeper than the top to clear the fixed footer.
//
// 22mm bottom and a 15mm footer are shared with the document builder
// (document-editor.scss / DocumentPDF), so the two page types sit the same
// distance off the paper edge.
export const PAGE_MARGIN_TOP_MM = 20;
export const PAGE_MARGIN_RIGHT_MM = 25;
export const PAGE_MARGIN_BOTTOM_MM = 22;
export const PAGE_MARGIN_LEFT_MM = 25;

/** Footer band (ACN/ABN line) sits inside the bottom margin, at the same
 * height as the Documents footers ($page-footer-bottom / DocumentPDF). */
export const PAGE_FOOTER_BOTTOM_MM = 10;

/** Body type: 10pt Tinos at 1.4, the metrics the issued PDFs have always used. */
export const FONT_SIZE_PT = 10;
export const LINE_HEIGHT = 1.4;

/**
 * Line-item metrics, in CSS px. One line of table type, which is also the
 * height of one blank line between lines, plus the air under the table's
 * header rule before the first row. The stylesheet reads these as custom
 * properties, the pagination adds them arithmetically, and InvoicePDF converts
 * them to points - so a break computed in the editor lands in the same place
 * in the export.
 */
export const LINE_ITEM_FONT_PX = 11;
export const LINE_ITEM_LINE_PX = 15.4; // LINE_ITEM_FONT_PX * LINE_HEIGHT
export const LINE_ITEM_FIRST_ROW_PAD_PX = 10;
/**
 * One indent level of a line item. The PDF printed 16pt (21.3px) per level
 * against the editor's 24px, so every indented line -- the RWD templates'
 * "As previously agreed." among them -- sat 2pt further left in the export
 * than on screen, and could wrap at a different word.
 */
export const LINE_ITEM_INDENT_PX = 24;

/**
 * A drawing line's columns: mark, drawing number, "Issue X", then the title,
 * which takes the rest of the description cell and wraps under itself. Fixed
 * widths are the whole point of the line type -- a run of drawings reads as a
 * table -- so the editor and the PDF must place each column at the same x.
 * Sized for the RWD numbering ("5910-13A-300", "CW-RET-100") with room to
 * spare; a number wider than its column runs on into the next on both
 * surfaces alike rather than wrapping (see InvoicePDF drawingNumberText).
 */
export const DRAWING_MARK_PX = 12;     // "-" plus the bullet's 6px gap; = InvoicePDF bulletMark
export const DRAWING_NUMBER_MM = 28;
/** The PDF's drawing-number text box: wider than any number, so it never breaks
 * (InvoicePDF drawingNumberText). PDF-only; the editor uses nowrap. */
export const DRAWING_NUMBER_OVERFLOW_MM = 150;
export const DRAWING_ISSUE_MM = 16;
/**
 * The "Issue" label inside the issue column, as a fixed width (about the word
 * plus a space at 11px Tinos). A long issue then wraps in the same width on
 * both surfaces, under itself; as a natural-width label followed by a space,
 * the PDF's second line started under "Issue" while the editor's started
 * under the value, and the two broke at different words.
 */
export const DRAWING_ISSUE_LABEL_PX = 26;

/** Header band padding, above and below the letterhead rule. */
export const HEADER_BAND_PAD_MM = 8;

/** Usable content width, i.e. the page less its side margins. */
export const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - PAGE_MARGIN_LEFT_MM - PAGE_MARGIN_RIGHT_MM;

// =======================================
// UNIT CONVERSION
// =======================================

export const mmToPt = (mm: number): number => mm * 2.83465;
/** CSS px (96dpi) to PDF pt (72dpi). */
export const pxToPt = (px: number): number => px * 0.75;
/** PDF pt to CSS px, for expressing PDF values in the editor's units. */
export const ptToPx = (pt: number): number => pt / 0.75;

// =======================================
// CSS CUSTOM PROPERTIES
// =======================================

/**
 * Applied to the editor root so the stylesheet can use the same numbers.
 * A static object, so this is a design-token injection rather than a
 * per-render inline style.
 */
export const INVOICE_PAGE_VARS: Record<string, string> = {
  '--invoice-page-width': `${PAGE_WIDTH_MM}mm`,
  '--invoice-page-height': `${PAGE_HEIGHT_MM}mm`,
  '--invoice-page-margin-top': `${PAGE_MARGIN_TOP_MM}mm`,
  '--invoice-page-margin-right': `${PAGE_MARGIN_RIGHT_MM}mm`,
  '--invoice-page-margin-bottom': `${PAGE_MARGIN_BOTTOM_MM}mm`,
  '--invoice-page-margin-left': `${PAGE_MARGIN_LEFT_MM}mm`,
  '--invoice-page-footer-bottom': `${PAGE_FOOTER_BOTTOM_MM}mm`,
  '--invoice-font-size': `${ptToPx(FONT_SIZE_PT)}px`,
  '--invoice-line-height': `${LINE_HEIGHT}`,
  '--invoice-line-item-font': `${LINE_ITEM_FONT_PX}px`,
  '--invoice-line-item-line': `${LINE_ITEM_LINE_PX}px`,
  '--invoice-line-item-first-row-pad': `${LINE_ITEM_FIRST_ROW_PAD_PX}px`,
  '--invoice-header-band-pad': `${HEADER_BAND_PAD_MM}mm`,
  '--invoice-drawing-mark': `${DRAWING_MARK_PX}px`,
  '--invoice-drawing-number': `${DRAWING_NUMBER_MM}mm`,
  '--invoice-drawing-issue': `${DRAWING_ISSUE_MM}mm`,
  '--invoice-drawing-issue-label': `${DRAWING_ISSUE_LABEL_PX}px`,
  '--invoice-line-item-indent': `${LINE_ITEM_INDENT_PX}px`,
};

// =======================================
// PAGE DISTRIBUTION
// =======================================

/**
 * How the editor split the invoice across pages. The PDF renders exactly this
 * plan rather than paginating on its own, which is the only way the two can be
 * guaranteed to break in the same places: react-pdf's line breaker and the
 * browser's will never agree to the pixel.
 */
export interface InvoicePagePlan {
  /** Indices into the invoice's line_items, in order. */
  rows: number[];
  /**
   * The closing block: payment details on the left, totals on the right.
   * They sit side by side and so are a single indivisible unit — splitting
   * them would leave half a row stranded on the previous page.
   */
  showSummary: boolean;
}
